import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';

import { scrollToTop, scrollToTopOfParent } from '../components/scrollUtils';
import MyGallery from '../components/MyGallery';
import EmailButton from '../components/EmailButton';

const RECIPIENT_EMAIL = 'Jennifer.Hillyard@thecommonroom.org.uk';

const hasValue = (value) => {
	if (value === null || value === undefined) {
		return false;
	}

	if (Array.isArray(value)) {
		return value.length > 0;
	}

	return String(value).trim() !== '';
};

const cleanCatalogueText = (value, removeLeadingSlash = false) => {
	if (!hasValue(value)) {
		return '';
	}

	let cleaned = String(value).trim();

	if (removeLeadingSlash) {
		cleaned = cleaned.replace(/^\/\s*/, '');
	}

	/*
	 * MARC-derived values commonly end in punctuation such as:
	 * "498 pages :", "illustrations ;" or "1st ed. /".
	 *
	 * Keep normal sentence-ending full stops, but remove catalogue delimiters.
	 */
	return cleaned.replace(/\s*[:;,/\\]+\s*$/, '').trim();
};

const formatDateRange = (fromDate, toDate) => {
	if (hasValue(fromDate) && hasValue(toDate)) {
		return fromDate === toDate ? fromDate : `${fromDate} — ${toDate}`;
	}

	return fromDate || toDate || '';
};

const isSafeHttpUrl = (value) => {
	return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
};

const normaliseChipValues = (value) => {
	if (!Array.isArray(value)) {
		return [];
	}

	return value
		.map((entry) => {
			if (entry === null || entry === undefined) {
				return '';
			}

			const cleaned = String(entry)
				.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
				.trim();

			/*
			 * CSV imports can leave an empty final value when a row ends in
			 * a comma. Also reject entries containing only separators.
			 */
			return /^[,;]*$/.test(cleaned) ? '' : cleaned;
		})
		.filter((entry) => entry !== '');
};

const fieldHasValue = (field) => {
	if (field.type === 'chips') {
		return normaliseChipValues(field.value).length > 0;
	}

	if (field.type === 'resources') {
		return Array.isArray(field.resources) && field.resources.some((resource) => (
			hasValue(resource?.url) ||
			hasValue(resource?.link_text) ||
			hasValue(resource?.public_note)
		));
	}

	return hasValue(field.value);
};

const MetadataField = ({ field, wide }) => {
	if (!fieldHasValue(field)) {
		return null;
	}

	const fullWidthClass = wide
		? 'sm:col-span-2 lg:col-span-3'
		: 'sm:col-span-2';

	const wrapperClass = field.full
		? `min-w-0 ${fullWidthClass}`
		: 'min-w-0';

	if (field.type === 'resources') {
		const resources = field.resources.filter((resource) => (
			hasValue(resource?.url) ||
			hasValue(resource?.link_text) ||
			hasValue(resource?.public_note)
		));

		return (
			<div className={wrapperClass}>
				<div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
					{field.label}
				</div>

				<div className="space-y-3">
					{resources.map((resource, index) => {
						const url = hasValue(resource.url) ? String(resource.url).trim() : '';
						const linkText = hasValue(resource.link_text)
							? String(resource.link_text).trim()
							: url;
						const publicNote = hasValue(resource.public_note)
							? String(resource.public_note).trim()
							: '';
						const safeUrl = isSafeHttpUrl(url);

						return (
							<div key={`${url}-${linkText}-${index}`} className="break-words">
								{safeUrl ? (
									<a
										href={url}
										target="_blank"
										rel="noopener noreferrer"
										className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
									>
										{linkText || url}
									</a>
								) : (
									<span className="font-semibold text-gray-900">
										{linkText || publicNote}
									</span>
								)}

								{publicNote && publicNote !== linkText && (
									<div className="mt-1 text-sm font-normal text-gray-600">
										{publicNote}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	if (field.type === 'chips') {
		const chipValues = normaliseChipValues(field.value);

		return (
			<div className={wrapperClass}>
				<div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
					{field.label}
				</div>

				<div className="flex flex-wrap gap-2">
					{chipValues.map((entry, index) => (
						<span
							key={`${entry}-${index}`}
							className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-800"
						>
							{entry}
						</span>
					))}
				</div>
			</div>
		);
	}

	const displayValue = Array.isArray(field.value)
		? field.value.join('; ')
		: field.value;

	return (
		<div className={wrapperClass}>
			<div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
				{field.label}
			</div>
			<div className="break-words text-base font-semibold text-gray-900">
				{displayValue}
			</div>
		</div>
	);
};

const MetadataGroup = ({ group, wide }) => {
	const populatedFields = group.fields.filter(fieldHasValue);

	if (populatedFields.length === 0) {
		return null;
	}

	const gridClass = wide
		? 'grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3'
		: 'grid gap-x-8 gap-y-6 sm:grid-cols-2';

	return (
		<section className="border-t border-gray-200 pt-6 first:border-t-0 first:pt-0">
			{group.title && (
				<h3 className="mb-5 text-xs font-bold uppercase tracking-widest text-gray-600">
					{group.title}
				</h3>
			)}

			<div className={gridClass}>
				{populatedFields.map((field) => (
					<MetadataField
						key={field.label}
						field={field}
						wide={wide}
					/>
				))}
			</div>
		</section>
	);
};

const buildDetailsGroups = (item) => {
	const koha = item.koha;

	if (koha) {
		return [
			{
				title: 'Bibliographic',
				fields: [
					{ label: 'Author', value: item.creator },
					{ label: 'Additional authors', value: item.authors },
					{
						label: 'Statement of responsibility',
						value: cleanCatalogueText(koha.statement_of_responsibility, true),
						full: true,
					},
					{ label: 'Alternative title', value: koha.alternative_title, full: true },
					{ label: 'Publisher', value: item.publisher },
					{ label: 'Edition', value: cleanCatalogueText(koha.edition) },
					{ label: 'ISBN', value: cleanCatalogueText(koha.isbn) },
					{ label: 'ISSN', value: cleanCatalogueText(koha.issn) },
					{ label: 'Language', value: item.language },
					{ label: 'Format', value: item.mediums },
					{ label: 'Barcode', value: koha.barcode },
					{ label: 'JISC NBK reference', value: koha.jisc_nbk_ref },
				],
			},
			{
				title: 'Series',
				fields: [
					{ label: 'Series name', value: koha.series_name },
					{ label: 'Series authority', value: koha.series_authority, full: true },
					{ label: 'Series number', value: cleanCatalogueText(koha.series_number) },
					{ label: 'Part of', value: koha.host_item, full: true },
				],
			},
			{
				title: 'Subjects',
				fields: [
					{
						label: 'Topics & places',
						value: item.keywords,
						type: 'chips',
						full: true,
					},
					{
						label: 'People',
						value: koha.subject_names,
						type: 'chips',
						full: true,
					},
					{
						label: 'Organisations',
						value: koha.subject_businesses,
						type: 'chips',
						full: true,
					},
				],
			},
			{
				title: 'Physical & location',
				fields: [
					{ label: 'Dimensions', value: item.dimensions },
					{ label: 'Pages / extent', value: cleanCatalogueText(koha.pages) },
					{
						label: 'Other details',
						value: cleanCatalogueText(koha.other_physical_details),
						full: true,
					},
					{ label: 'Classmark', value: item.classmark },
					{ label: 'Location', value: koha.location },
					{ label: 'Bound with', value: koha.bound_with, full: true },
				],
			},
			{
				title: 'Notes & resources',
				fields: [
					{ label: 'General notes', value: koha.general_notes, full: true },
					{ label: 'Acquisition source', value: koha.acquisition_source },
					{ label: 'Catalogue image reference', value: koha.image_name },
					{
						label: 'Online resources',
						type: 'resources',
						resources: item.resources,
						full: true,
					},
				],
			},
		];
	}

	return [
		{
			title: 'Bibliographic',
			fields: [
				{ label: 'Creator', value: item.creator },
				{ label: 'Additional authors', value: item.authors },
				{ label: 'Publisher', value: item.publisher },
				{ label: 'Language', value: item.language },
				{ label: 'Format', value: item.mediums },
			],
		},
		{
			title: 'Dates & identification',
			fields: [
				{ label: 'From date', value: item.date_from_uk_format },
				{ label: 'To date', value: item.date_to_uk_format },
				{ label: 'Classmark', value: item.classmark },
				{ label: 'Previous identifier', value: item.previous_identifier },
			],
		},
		{
			title: 'Physical & location',
			fields: [
				{ label: 'Dimensions', value: item.dimensions },
				{ label: 'Interior location', value: item.interior_location },
			],
		},
		{
			title: 'Subjects',
			fields: [
				{
					label: 'Keywords',
					value: item.keywords,
					type: 'chips',
					full: true,
				},
			],
		},
		{
			title: 'Notes & resources',
			fields: [
				{ label: 'Related material', value: item.related_material, full: true },
				{ label: 'Resource information', value: item.resource_information, full: true },
				{
					label: 'Online resources',
					type: 'resources',
					resources: item.resources,
					full: true,
				},
			],
		},
	];
};

const CategoryBreadcrumbs = ({ categories }) => {
	if (!Array.isArray(categories) || categories.length === 0) {
		return null;
	}

	return (
		<nav aria-label="Object category" className="text-sm text-gray-200">
			{categories.map((category, index) => {
				const categoryLabel = categories
					.slice(0, index + 1)
					.map((entry) => entry.name)
					.join('/');

				return (
					<React.Fragment key={category.id || index}>
						<Link
							to={`/?category=${category.id}&category_label=${encodeURIComponent(categoryLabel)}`}
							className="underline decoration-gray-500 underline-offset-4 hover:text-white"
						>
							{category.name}
						</Link>

						{index < categories.length - 1 && (
							<span className="mx-2 text-gray-500" aria-hidden="true">/</span>
						)}
					</React.Fragment>
				);
			})}
		</nav>
	);
};

const ViewObject = () => {
	const [item, setItem] = useState({});
	const [assets, setAssets] = useState([]);
	const [relatedObjects, setRelatedObjects] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [articlesOpen, setArticlesOpen] = useState(false);

	const { id } = useParams();
	const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

	useEffect(() => {
		const controller = new AbortController();

		setLoading(true);
		setError('');
		setArticlesOpen(false);

		const fetchItemDetails = async () => {
			try {
				const response = await fetch(
					`${apiEndpoint}/viewObject/${id}`,
					{ signal: controller.signal }
				);

				if (!response.ok) {
					throw new Error(
						response.status === 404
							? 'This item could not be found.'
							: 'The item could not be loaded.'
					);
				}

				const json = await response.json();

				setItem(json.object || {});
				setAssets(Array.isArray(json.assets) ? json.assets : []);
				setRelatedObjects(
					Array.isArray(json.related_objects)
						? json.related_objects
						: []
				);

				scrollToTop();
				scrollToTopOfParent();
			} catch (fetchError) {
				if (fetchError.name !== 'AbortError') {
					console.error('Error fetching item details:', fetchError);
					setError(fetchError.message || 'The item could not be loaded.');
					setItem({});
					setAssets([]);
					setRelatedObjects([]);
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		};

		fetchItemDetails();

		return () => controller.abort();
	}, [id, apiEndpoint]);

	const imageAssets = assets.filter((asset) => (
		asset.is_image === true ||
		(
			asset.is_pdf !== true &&
			typeof asset.file_type === 'string' &&
			asset.file_type.toLowerCase().startsWith('image/')
		)
	));

	const pdfAssets = assets.filter((asset) => (
		asset.is_pdf === true ||
		asset.file_type === 'application/pdf'
	));

	const hasImages = imageAssets.length > 0;
	const hasSingleImage = imageAssets.length === 1;
	const hasMultipleImages = imageAssets.length > 1;
	const pdfDownloadUrl = item.pdf_download_url || pdfAssets[0]?.download_url || '';
	const dateRange = formatDateRange(
		item.date_from_uk_format,
		item.date_to_uk_format
	);
	const detailsGroups = buildDetailsGroups(item);
	const populatedGroups = detailsGroups.filter((group) => (
		group.fields.some(fieldHasValue)
	));
	const articles = Array.isArray(item.koha?.articles)
		? item.koha.articles.filter((article) => (
			hasValue(article?.title) || hasValue(article?.author)
		))
		: [];
	const articleCount = item.koha?.article_count ?? articles.length;


	/*
	 * Descriptions may contain formatting entered in the CMS or imported from
	 * catalogue data. Sanitise the HTML before rendering it so stored content
	 * cannot execute scripts, event handlers or unsafe attributes.
	 */
	const sanitisedDescription = useMemo(() => {
		if (!hasValue(item.description)) {
			return '';
		}

		return DOMPurify.sanitize(String(item.description), {
			ALLOWED_TAGS: [
				'p', 'br',
				'strong', 'b', 'em', 'i', 'u',
				'ul', 'ol', 'li',
				'blockquote',
				'h2', 'h3', 'h4', 'h5', 'h6',
				'a',
			],
			ALLOWED_ATTR: ['href', 'title'],
			ALLOW_DATA_ATTR: false,
		});
	}, [item.description]);

	return (
		<>
			<Helmet>
				<title>{item.title || 'Object'}</title>
				<meta
					name="description"
					content={item.title || 'Collection object'}
				/>
			</Helmet>

			{loading && (
				<div className="mx-auto max-w-screen-2xl bg-white px-4">
					<div className="loader-container">
						<div className="loader"></div>
					</div>
				</div>
			)}

			{!loading && error && (
				<div className="mx-auto max-w-screen-xl px-5 py-16">
					<h1 className="text-3xl font-bold text-gray-900">
						Item unavailable
					</h1>
					<p className="mt-4 text-lg text-gray-600">{error}</p>
				</div>
			)}

			{!loading && !error && (
				<div>
					<header className="bg-black px-5 text-white">
						<div
							className={`mx-auto max-w-screen-xl py-10 ${
								hasSingleImage
									? 'grid items-center gap-10 md:grid-cols-2 lg:gap-16 lg:py-8'
									: ''
							}`}
						>
							<div className={hasSingleImage ? 'max-w-2xl' : ''}>
								{hasValue(item.identifier) && (
									<div className="flex items-center gap-3 text-gray-400">
										<span className="border border-gray-600 px-2 py-1 text-xs font-semibold uppercase tracking-wider">
											ID
										</span>
										<span>{item.identifier}</span>
									</div>
								)}

								<div className="mt-4">
									<CategoryBreadcrumbs categories={item.categories} />
								</div>

								<h1 className="mt-8 max-w-4xl text-3xl font-bold leading-tight sm:text-4xl">
									{item.title}
									{hasValue(item.page_range) && (
										<span className="font-normal"> ({item.page_range})</span>
									)}
								</h1>

								{hasValue(dateRange) && (
									<div className="mt-6 flex items-center gap-3 text-gray-200">
										<span className="border border-gray-600 px-2 py-1 text-xs font-semibold uppercase tracking-wider">
											Date
										</span>
										<span>{dateRange}</span>
									</div>
								)}

								<div className="mt-7 flex flex-wrap gap-3">
									{pdfDownloadUrl && (
										<a
											href={pdfDownloadUrl}
											download
											className="inline-flex items-center gap-2 border border-white bg-white px-5 py-3 font-semibold text-black hover:bg-gray-200"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
												strokeWidth="1.8"
												stroke="currentColor"
												className="h-5 w-5"
												aria-hidden="true"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14"
												/>
											</svg>
											Download PDF
										</a>
									)}

									<EmailButton
										buttonText="Request a Copy"
										recipientEmail={RECIPIENT_EMAIL}
										subject="Request a Copy"
										identifier={item.identifier}
										className="inline-flex items-center border border-white bg-transparent px-5 py-3 font-semibold text-white hover:bg-white hover:text-black"
									/>

									<EmailButton
										buttonText="Request a Visit"
										recipientEmail={RECIPIENT_EMAIL}
										subject="Request a Visit"
										identifier={item.identifier}
										className="inline-flex items-center border border-white bg-transparent px-5 py-3 font-semibold text-white hover:bg-white hover:text-black"
									/>
								</div>
							</div>

							{hasSingleImage && (
								<div className="w-full max-w-[400px] justify-self-center md:justify-self-end">
									<MyGallery
										slides={imageAssets}
										referenceId={item.identifier || item.id}
										objectTitle={item.title}
										variant="hero"
									/>
								</div>
							)}
						</div>
					</header>

					<main className="bg-white">
						<div className="mx-auto max-w-screen-xl px-5 pb-16">
							{hasMultipleImages && (
								<section className="pt-8" aria-labelledby="images-heading">
									<div className="mb-5 flex items-baseline gap-3">
										<h2 id="images-heading" className="text-2xl font-bold">
											Images
										</h2>
										<span className="text-sm font-medium text-gray-500">
											· {imageAssets.length}
										</span>
									</div>

									<MyGallery
										slides={imageAssets}
										referenceId={item.identifier || item.id}
										objectTitle={item.title}
									/>
								</section>
							)}

							<div className="mt-10 space-y-10">
								<section aria-labelledby="description-heading">
									<h2 id="description-heading" className="mb-5 text-2xl font-bold">
										Description
									</h2>

									{hasValue(item.description) ? (
										<>
											<div
												className="w-full text-base leading-7 text-gray-800 [&>p]:mb-4"
												dangerouslySetInnerHTML={{ __html: sanitisedDescription }}
											/>

											{item.description_is_generated && (
												<p className="mt-3 text-sm text-gray-500">
													Summary generated from catalogue fields.
												</p>
											)}
										</>
									) : (
										<p className="italic text-gray-500">
											No description recorded for this item.
										</p>
									)}
								</section>

								{populatedGroups.length > 0 && (
									<section aria-labelledby="details-heading">
										<h2 id="details-heading" className="mb-5 text-2xl font-bold">
											Further details
										</h2>

										<div className="space-y-7 border border-gray-200 bg-gray-50 p-6 sm:p-7">
											{populatedGroups.map((group) => (
												<MetadataGroup
													key={group.title || 'details'}
													group={group}
													wide
												/>
											))}
										</div>
									</section>
								)}
							</div>

							{articles.length > 0 && (
								<details
									key={item.id}
									className="mt-10 border border-gray-200"
									onToggle={(event) => setArticlesOpen(event.currentTarget.open)}
								>
									<summary className="flex cursor-pointer list-none items-center gap-3 bg-gray-50 px-5 py-4 text-lg font-bold marker:hidden">
										<span
											className={`text-sm transition-transform ${
												articlesOpen ? 'rotate-90' : ''
											}`}
											aria-hidden="true"
										>
											▶
										</span>
										<span>Contents &amp; related articles</span>
										<span className="ml-auto text-sm font-semibold text-gray-500">
											{articleCount} {articleCount === 1 ? 'entry' : 'entries'}
										</span>
									</summary>

									{articlesOpen && (
										<div className="px-5 pb-4">
											{articles.map((article, index) => {
												const title = cleanCatalogueText(article.title);
												const author = cleanCatalogueText(article.author, true);

												return (
													<div
														key={`${title}-${author}-${index}`}
														className="border-b border-gray-200 py-4 last:border-b-0"
													>
														{title && (
															<div className="font-semibold text-gray-900">
																<span className="mr-2 text-xs font-bold text-gray-400">
																	{index + 1}
																</span>
																{title}
															</div>
														)}

														{author && (
															<div className="mt-1 pl-6 text-sm text-gray-600">
																{author}
															</div>
														)}
													</div>
												);
											})}
										</div>
									)}
								</details>
							)}
						</div>
					</main>

					{relatedObjects.length > 0 && (
						<section className="border-t-2 border-gray-100 bg-white px-5 py-10">
							<div className="mx-auto max-w-screen-xl">
								<h2 className="mb-6 text-2xl font-bold">Related objects</h2>

								<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
									{relatedObjects.map((relatedObject) => (
										<Link
											key={relatedObject.id}
											to={`/object/${relatedObject.id}`}
											className="overflow-hidden border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
										>
											{relatedObject.featured_asset_thumbnail && (
												<img
													src={relatedObject.featured_asset_thumbnail}
													className="h-52 w-full object-cover"
													alt={relatedObject.title || ''}
													title={relatedObject.title || ''}
													loading="lazy"
												/>
											)}

											<div className="p-5">
												<div className="text-lg font-semibold">
													{relatedObject.title}
												</div>
												<div className="mt-1 text-sm text-gray-500">
													{relatedObject.identifier}
												</div>
											</div>
										</Link>
									))}
								</div>
							</div>
						</section>
					)}
				</div>
			)}
		</>
	);
};

export default ViewObject;
